import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { productContractAPI, productAPI } from "@/lib/api";
import { toast } from "sonner";
import { DateField } from "@/components/ui/date-field";
import { DocumentItemsEditor } from "@/components/document-form/DocumentItemsEditor";
import { usePaymentMethods } from "@/lib/paymentMethods";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

const LEGACY_TVA = 20;
const newLine = () => ({ _key: crypto.randomUUID(), product_id: "", reference: "", product_name: "", description: "", quantity: 1, unit_price: 0, tva_rate: LEGACY_TVA });
const defaultForm = { start_date: "", end_date: "", next_billing_date: "", subscription_mode: "Annuel", payment_mode: "", is_active: true };

/** Ligne de contrat (ancien ou nouveau format) -> ligne de l'éditeur de factures. Conversion à l'affichage seulement. */
const toEditorLine = (it) => ({
  _key: crypto.randomUUID(),
  product_id: it.product_id || "",
  reference: it.reference || "",
  product_name: it.product_name || "",
  description: it.description ?? it.label ?? "",
  quantity: Number(it.quantity ?? 1) || 0,
  unit_price: Number(it.unit_price ?? 0) || 0,
  tva_rate: it.tva_rate ?? LEGACY_TVA,
});

const contractToForm = (c) => {
  const items = c.items?.length
    ? c.items.map(toEditorLine)
    : [toEditorLine({ product_name: c.product_name, label: c.label, unit_price: c.client_price, quantity: 1 })];
  return { ...c, items };
};

const stripKey = ({ _key, ...line }) => line; // eslint-disable-line no-unused-vars

export default function ContractForm({ open, onOpenChange, clientId, contractId, contractData, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ...defaultForm, items: [newLine()] });
  const [products, setProducts] = useState([]);
  const pm = usePaymentMethods();
  const isEditing = !!(contractId || contractData);

  const refreshProducts = useCallback(() => {
    productAPI.list({ limit: 5000 }).then(r => setProducts(r.data?.items || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    refreshProducts();
    if (contractData) {
      setForm(contractToForm(contractData));
      setLoading(false);
    } else if (contractId) {
      setLoading(true);
      productContractAPI.get(contractId)
        .then(r => setForm(contractToForm(r.data)))
        .catch(() => toast.error("Erreur chargement du contrat"))
        .finally(() => setLoading(false));
    } else {
      setForm({ ...defaultForm, items: [newLine()] });
      setLoading(false);
    }
  }, [open, contractId, contractData, refreshProducts]);

  // Mode présélectionné : code déjà enregistré, sinon correspondance calculée par le serveur, sinon valeur brute.
  const isCode = (v) => pm.options.some(m => m.value === v);
  const paymentMode = isCode(form.payment_mode) ? form.payment_mode
    : (isCode(form.payment_method_code) ? form.payment_method_code : form.payment_mode || "");

  const save = async () => {
    try {
      const items = form.items.filter(it => it.product_name || it.description || it.unit_price).map(stripKey);
      const { payment_method_code, ...rest } = form; // eslint-disable-line no-unused-vars
      const data = { ...rest, items, payment_mode: paymentMode, client_id: clientId || form.client_id };
      if (items.length > 0) {
        data.product_name = items[0].product_name;
        data.label = items.length > 1 ? `${items.length} lignes` : items[0].description || "";
      }
      const id = contractId || contractData?.id;
      if (id) {
        await productContractAPI.update(id, data);
        toast.success("Contrat mis à jour");
      } else {
        await productContractAPI.create(data);
        toast.success("Contrat créé");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const updateItem = (idx, k, v) => setForm(prev => {
    const items = [...prev.items];
    items[idx] = { ...items[idx], [k]: v };
    return { ...prev, items };
  });
  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, newLine()] }));
  const removeItem = (idx) => setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  const duplicateItem = (idx) => setForm(prev => {
    const items = [...prev.items];
    items.splice(idx + 1, 0, { ...items[idx], _key: crypto.randomUUID() });
    return { ...prev, items };
  });
  const totalHT = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader><DialogTitle>{isEditing ? "Modifier le contrat" : "Nouveau contrat"}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin h-6 w-6 text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Prochaine facturation</Label>
                <DateField value={form.next_billing_date || ""} onChange={e => setField("next_billing_date", e.target.value)} data-testid="contract-next-billing" />
              </div>
              <div>
                <Label>Periodicite</Label>
                <Select value={form.subscription_mode} onValueChange={v => setField("subscription_mode", v)}>
                  <SelectTrigger data-testid="contract-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mensuel">Mensuel</SelectItem>
                    <SelectItem value="Trimestriel">Trimestriel</SelectItem>
                    <SelectItem value="Semestriel">Semestriel</SelectItem>
                    <SelectItem value="Annuel">Annuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Mode de règlement</Label>
                <Select value={paymentMode} onValueChange={v => setField("payment_mode", v)}>
                  <SelectTrigger data-testid="contract-payment-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {pm.optionsWith(paymentMode).map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch checked={form.is_active !== false} onCheckedChange={v => setField("is_active", v)} data-testid="contract-active-switch" />
                <Label className="text-sm">{form.is_active !== false ? "Actif" : "Inactif"}</Label>
              </div>
            </div>
            <div data-testid="contract-items-editor">
              <DocumentItemsEditor
                items={form.items}
                products={products}
                onAdd={addItem}
                onUpdate={updateItem}
                onRemove={removeItem}
                onDuplicate={duplicateItem}
                onProductCreated={refreshProducts}
              />
              <div className="flex justify-end mt-2">
                <span className="text-sm font-semibold text-slate-700" data-testid="contract-total-ht">Total HT : {formatEur(totalHT)}</span>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[60px] resize-y"
                placeholder="Notes internes ou commentaires..."
                value={form.notes || ""}
                onChange={e => setField("notes", e.target.value)}
                data-testid="contract-notes"
              />
            </div>
            <Button onClick={save} className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="contract-save-btn">
              {isEditing ? "Mettre à jour" : "Creer le contrat"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
